import React, { useState, useEffect } from 'react';
import { Plus, Search, Clock, ChevronDown, Pencil, Trash2, GripVertical } from 'lucide-react';
import EmptyState from '../../components/EmptyState';
import FlatRateModal from '../../components/FlatRateModal';
import { getFlatRateItems, getFlatRateCategories, deleteFlatRateItem, FlatRateItem, FlatRateCategory } from '../../lib/flat-rates';
import { useBusiness } from '../../contexts/BusinessContext';

export default function FlatRates() {
  const { selectedBusiness } = useBusiness();
  const [items, setItems] = useState<FlatRateItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<FlatRateItem | null>(null);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [categories, setCategories] = useState<FlatRateCategory[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640); // sm breakpoint
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadItems = async () => {
    if (!selectedBusiness?.id) return;

    try {
      setLoading(true);
      const [itemsData, categoriesData] = await Promise.all([
        getFlatRateItems(selectedBusiness.id),
        getFlatRateCategories(selectedBusiness.id)
      ]);
      setItems(itemsData);
      setCategories(categoriesData);
      setError(null);
      
      // Categories are collapsed by default for cleaner UI
      // Users can expand them as needed
    } catch (err) {
      console.error('Error loading flat rate items:', err);
      setError(err instanceof Error ? err.message : 'Failed to load flat rate items');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, [selectedBusiness?.id, isMobile]);

  const handleAddItem = () => {
    setSelectedItem(null);
    setModalMode('create');
    setIsModalOpen(true);
  };

  const handleEditItem = (item: FlatRateItem) => {
    setSelectedItem(item);
    setModalMode('edit');
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      setLoading(true);
      await deleteFlatRateItem(id);
      await loadItems();
    } catch (err) {
      console.error('Error deleting flat rate item:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete flat rate item');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleAllCategories = () => {
    if (expandedCategories.size === itemsByCategory.length) {
      // Collapse all
      setExpandedCategories(new Set());
    } else {
      // Expand all
      const allIds = itemsByCategory.map(cat => cat.id);
      setExpandedCategories(new Set(allIds));
    }
  };

  const filteredItems = items.filter(item => {
    const searchLower = searchQuery.toLowerCase();
    return (
      item.name.toLowerCase().includes(searchLower) ||
      item.description?.toLowerCase().includes(searchLower)
    );
  });

  // Group items by category
  const itemsByCategory = React.useMemo(() => {
    const grouped = items.reduce((acc, item) => {
      const categoryId = item.category_id || 'uncategorized';
      if (!acc[categoryId]) {
        acc[categoryId] = {
          id: categoryId,
          name: item.category?.name || 'Uncategorized',
          items: []
        };
      }
      acc[categoryId].items.push(item);
      return acc;
    }, {} as Record<string, { id: string; name: string; items: FlatRateItem[] }>);

    return Object.values(grouped).sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  return (
    <div 
      className="h-full flex flex-col bg-gray-100 overflow-hidden"
      style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' }}
    >
      <div className="flex-1 overflow-y-auto">
        <div className="p-2 sm:p-4">
          <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6 pb-24">
        <div className="bg-gray-50 rounded-2xl shadow-lg border border-gray-200/50 overflow-hidden">
          {/* Mobile-optimized search section */}
          <div className="px-4 sm:px-8 py-4 sm:py-6 border-b border-gray-200/50">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
              <div className="relative flex-1 sm:max-w-md">
                <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 text-sm sm:text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
                />
              </div>
              <div className="flex items-center space-x-3">
                <button 
                  onClick={handleAddItem}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2.5 sm:py-2 text-sm font-semibold text-white bg-purple-700 rounded-xl hover:bg-purple-800 transition-all duration-200 active:scale-95 shadow-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Item
                </button>
                {/* Desktop expand/collapse all button */}
                {!isMobile && itemsByCategory.length > 1 && (
                  <button
                    onClick={toggleAllCategories}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium px-3 py-2 rounded-lg hover:bg-purple-50 transition-colors duration-200"
                  >
                    {expandedCategories.size === itemsByCategory.length ? 'Collapse All' : 'Expand All'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-48 sm:h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-purple-600 mx-auto"></div>
                <p className="mt-3 sm:mt-4 text-sm sm:text-base text-gray-600">Loading items...</p>
              </div>
            </div>
          ) : error ? (
            <div className="p-4 sm:p-8">
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 sm:px-6 py-3 sm:py-4 rounded-xl text-sm sm:text-base">
                {error}
              </div>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex items-center justify-center h-48 sm:h-64">
              <div className="text-center py-8 sm:py-12 px-4">
                <Clock className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <p className="text-base sm:text-lg font-semibold text-gray-900">No flat rate items</p>
                <p className="text-sm sm:text-base text-gray-500 mt-1 sm:mt-2">
                  Start adding flat rate items to track resource hours.
                </p>
                <button
                  onClick={handleAddItem}
                  className="mt-4 sm:mt-6 w-full sm:w-auto inline-flex items-center justify-center px-6 py-3 text-sm sm:text-base font-semibold text-white bg-purple-700 hover:bg-purple-800 rounded-xl transition-all duration-200 active:scale-95"
                >
                  <Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                  Add your first item
                </button>
              </div>
            </div>
          ) : (
            <div className="p-4 sm:p-8 space-y-4 sm:space-y-6">
              {itemsByCategory.map((category) => (
                <div key={category.id} className="bg-white rounded-2xl border border-gray-200/50 shadow-sm overflow-hidden">
                  {/* Enhanced category header */}
                  <div 
                    className={`px-4 sm:px-6 py-4 sm:py-4 bg-gray-50 border-b border-gray-200/50 flex items-center justify-between transition-all duration-200 cursor-pointer hover:bg-gray-100 active:bg-gray-200 touch-manipulation`}
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center space-x-3 sm:space-x-3">
                      <ChevronDown 
                        className={`h-5 w-5 sm:h-5 sm:w-5 text-gray-400 transition-all duration-300 ease-out ${
                          expandedCategories.has(category.id) ? 'rotate-180 text-purple-500' : ''
                        }`}
                      />
                      <h3 className="text-base sm:text-base font-semibold text-gray-900">{category.name}</h3>
                      <span className="text-xs sm:text-sm text-gray-500 bg-gray-200 px-2.5 py-1 rounded-full font-medium min-w-[24px] text-center">
                        {category.items.length}
                      </span>
                    </div>
                    {!isMobile && (
                      <div className="text-xs text-gray-400 font-medium">
                        {expandedCategories.has(category.id) ? 'Click to collapse' : 'Click to expand'}
                      </div>
                    )}
                  </div>
                  
                  {/* Enhanced collapsible content */}
                  <div className={`transition-all duration-300 ease-out ${
                    expandedCategories.has(category.id) 
                      ? 'opacity-100' 
                      : 'max-h-0 opacity-0 overflow-hidden'
                  }`}>
                    <div className="divide-y divide-gray-200/50">
                      {category.items.map((item) => (
                        <div key={item.id} className="p-4 sm:p-6 hover:bg-gray-50 transition-colors duration-200">
                          {/* Mobile-optimized item layout */}
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between space-y-3 sm:space-y-0">
                            <div className="flex items-start space-x-2 sm:space-x-4 flex-1 min-w-0">
                              {/* Hide grip handle on mobile */}
                              <button className="hidden sm:block cursor-grab active:cursor-grabbing p-2 -ml-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-200">
                                <GripVertical className="h-5 w-5" />
                              </button>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm sm:text-base font-semibold text-gray-900 truncate">{item.name}</h3>
                                <div className="mt-2 sm:mt-3 flex items-center text-xs sm:text-sm font-medium text-gray-500">
                                  <Clock className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                                  {item.hours} hours
                                </div>
                              </div>
                            </div>
                            {/* Mobile-optimized action buttons */}
                            <div className="flex items-center justify-end space-x-2 sm:space-x-2">
                              <button
                                onClick={() => handleEditItem(item)}
                                className="p-2.5 sm:p-2 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-100 transition-all duration-200 active:scale-95 touch-manipulation"
                              >
                                <Pencil className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="p-2.5 sm:p-2 text-red-400 hover:text-red-600 rounded-xl hover:bg-red-50 transition-all duration-200 active:scale-95 touch-manipulation"
                              >
                                <Trash2 className="h-4 w-4 sm:h-5 sm:w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <FlatRateModal
            isOpen={isModalOpen}
            onClose={() => {
              setIsModalOpen(false);
              setSelectedItem(null);
            }}
            onSuccess={() => {
              setIsModalOpen(false);
              setSelectedItem(null);
              loadItems();
            }}
            businessId={selectedBusiness?.id}
            item={selectedItem}
            mode={modalMode}
          />
        </div>
      </div>
    </div>
      </div>
    </div>
  );
}